'use client'

import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useMutation } from '@tanstack/react-query'
import { MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { GridSection } from '@/components/marketing/fragments/grid-section'
import { SiteHeading } from '@/components/marketing/fragments/site-heading'
import { AnimatedGridPattern } from '@/components/ui/animated-grid-pattern'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { BlogService } from '@/lib/api'
import { Grid } from '@/components/blocks/feature-section-with-card-gradient'

type FormData = {
  firstname: string
  lastname: string
  email: string
  message: string
}

type ContactInfoProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any
  text: string
}

function ContactInfo({ icon: Icon, text }: ContactInfoProps): React.JSX.Element {
  return (
    <div className='flex items-start gap-2 text-sm lg:w-80'>
      <Icon className='size-4 shrink-0 text-muted-foreground mt-0.5' />
      <span>{text}</span>
    </div>
  )
}

export function Contact(): React.JSX.Element {
  const { i18n } = useLingui()
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      message: ''
    }
  })

  const mutation = useMutation({
    mutationFn: (data: FormData) =>
      BlogService.createContact({
        name: `${data.firstname} ${data.lastname}`,
        email: data.email,
        subject: 'Contact Form Submission',
        content: data.message
      }),
    onSuccess: () => {
      toast.success(i18n._(msg`Contact form submitted successfully!`))
      reset()
    }
  })

  const onSubmit = (data: FormData) => {
    mutation.mutate(data)
  }

  return (
    <div className='relative'>
      <AnimatedGridPattern />
      <GridSection>
        <div className='bg-diagonal-lines'>
          <div className='flex flex-col bg-background md:px-12 px-4 py-20 md:py-24 lg:mx-12 lg:border-x overflow-hidden'>
            <SiteHeading badge={msg`Contact`} title={msg`We love to hear from you!`} />
            <div className='lg:container lg:max-w-6xl '>
              <div className='flex flex-col justify-between gap-10 lg:flex-row lg:gap-20'>
                <div className='order-2 space-y-8 text-center lg:order-1 lg:w-1/2 lg:text-left z-10'>
                  <h3 className='mb-1 hidden max-w-fit text-4xl font-semibold lg:block'>
                    {i18n._(`Get in touch`)}
                  </h3>
                  <p className='text-muted-foreground lg:max-w-[80%]'>
                    {i18n._(
                      `If you have any questions, don't hesitate to contact our team. We'll get back to you within 48 hours.`
                    )}
                  </p>
                  <div className='space-y-4'>
                    <h4 className='hidden text-lg font-medium lg:block'>
                      {i18n._(`Contact details`)}
                    </h4>
                    <div className='flex flex-col items-center gap-3 lg:items-start'>
                      <ContactInfo icon={PhoneIcon} text='(+84) 28 3535 5888' />
                      <ContactInfo icon={PhoneIcon} text='(+84) 901 419 949' />
                      <ContactInfo icon={MailIcon} text='info@pionegroup.com' />
                      <div className='flex items-start gap-2 text-sm'>
                        <MapPinIcon className='size-4 shrink-0 text-muted-foreground mt-0.5' />
                        <p>
                          <strong>{i18n._('Head office:')} </strong>
                          {i18n._(
                            '304/18 Tan Ky Tan Quy Street, Tan Son Nhi Ward, Tan Phu District, Ho Chi Minh City, Vietnam'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <Card className='order-1 overflow-hidden relative mx-auto rounded-3xl w-full max-w-lg shadow-lg lg:order-2 lg:w-1/2  z-10'>
                  <Grid size={30} />
                  <CardContent className='flex flex-col gap-6 p-6 lg:p-10'>
                    <div className='grid grid-cols-2 gap-4'>
                      <div className='col-span-2 grid w-full items-center gap-1.5 sm:col-span-1'>
                        <Label htmlFor='firstname'>{i18n._(`First Name`)}</Label>
                        <Input
                          id='firstname'
                          type='text'
                          {...register('firstname', {
                            required: i18n._(`First Name is required`)
                          })}
                          disabled={mutation.isPending}
                        />
                        {errors.firstname && (
                          <p className='mt-0.5 text-xs text-red-500'>{errors.firstname.message}</p>
                        )}
                      </div>
                      <div className='col-span-2 grid w-full items-center gap-1.5 sm:col-span-1'>
                        <Label htmlFor='lastname'>{i18n._(`Last Name`)}</Label>
                        <Input
                          id='lastname'
                          type='text'
                          {...register('lastname', {
                            required: i18n._(`Last Name is required`)
                          })}
                          disabled={mutation.isPending}
                        />
                        {errors.lastname && (
                          <p className='mt-0.5 text-xs text-red-500'>{errors.lastname.message}</p>
                        )}
                      </div>
                    </div>
                    <div className='grid w-full items-center gap-1.5'>
                      <Label htmlFor='email'>{i18n._(`Email`)}</Label>
                      <Input
                        id='email'
                        type='email'
                        {...register('email', {
                          required: i18n._(`Email is required`),
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: i18n._(`Invalid email address`)
                          }
                        })}
                        disabled={mutation.isPending}
                      />
                      {errors.email && (
                        <p className='mt-0.5 text-xs text-red-500'>{errors.email.message}</p>
                      )}
                    </div>
                    <div className='grid w-full gap-1.5'>
                      <Label htmlFor='message'>{i18n._(`Message`)}</Label>
                      <Textarea
                        className='rounded-xl'
                        id='message'
                        {...register('message', { required: i18n._(`Message is required`) })}
                        placeholder={i18n._(`Type your message here.`)}
                        rows={6}
                        disabled={mutation.isPending}
                      />
                      {errors.message && (
                        <p className='mt-0.5 text-xs text-red-500'>{errors.message.message}</p>
                      )}
                    </div>
                    <Button
                      type='button'
                      className='w-full rounded-xl'
                      onClick={handleSubmit(onSubmit)}
                      disabled={mutation.isPending}
                    >
                      {mutation.isPending ? i18n._(`Submitting...`) : i18n._(msg`Send message`)}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </GridSection>
    </div>
  )
}
